import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { SmartToy as SmartToyIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { tutorTokens, visuallyHidden } from '../../theme/tutorTheme';
import { adminColors } from '../../theme/adminTheme';

// ─────────────────────────────────────────────────────────────────────────
// AgentTutorComingSoon — the one placeholder every role lands on while
// Agent Tutor is held back (admin/teacher tab, and both student routes).
// One component, one piece of copy, so the experience is identical no
// matter which surface a person reaches it from.
//
// Deliberately static: a mark, an eyebrow, a heading naming the feature,
// and one sentence of plain copy. No progress bar, no email capture, no
// button that goes nowhere — this project's copy guidance is explicit that
// a dead control reads worse than no control.
//
// Colors, the eyebrow/body type roles, and the horizontal gutter come from
// `tutorTokens`, matching every other Agent Tutor surface. `minHeight`, the
// badge size, `maxWidth`, and the vertical padding are this component's own
// — they're not token values, just this placeholder's specific proportions.
//
// Two layout modes, controlled by `standalone`:
//  - embedded (default) — for AdminDashboardV2's `main` content area, which
//    already has its own `minHeight: 100vh` plus a `Toolbar` above this. A
//    second full-viewport minimum stacked inside that would force an
//    unwanted scrollbar, so this mode uses a generous fixed band instead.
//    The admin sidebar is the surface's own way back; no header is rendered
//    here.
//  - standalone — for the two bare student routes (`/tutor`, `/tutor/:id`),
//    which mount this with no other chrome at all (both early-return before
//    their real page's header would otherwise fetch/render). This mode
//    fills the viewport itself and renders the same dark "back to chat" bar
//    the real student pages use, so the state is never a dead end — and
//    puts `tutorTokens.surface` on its own outermost box so no unstyled
//    browser-white can show beneath a short viewport.
// ─────────────────────────────────────────────────────────────────────────

const BODY_TEXT = "Agent Tutor is in development and will be available here once it's ready.";

const focusRingSx = {
  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
};

const AgentTutorComingSoon = ({ standalone = false, showHeading = true }) => {
  const navigate = useNavigate();

  const content = (
    <Box sx={{ maxWidth: 460, width: '100%', textAlign: 'center' }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: 'auto',
          mb: 3,
          borderRadius: '50%',
          backgroundColor: tutorTokens.accentLight,
          border: `1px solid ${tutorTokens.accentBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SmartToyIcon aria-hidden="true" sx={{ fontSize: 32, color: tutorTokens.accent }} />
      </Box>

      <Typography
        sx={{
          ...tutorTokens.type.eyebrow,
          fontFamily: tutorTokens.fontUI,
          color: tutorTokens.accent,
          mb: 1.5,
        }}
      >
        COMING SOON
      </Typography>

      {/* Admin context suppresses the visible heading (the AppBar above this
          content area already renders "Agent Tutor" as the page title) but
          still needs a real heading in the a11y tree — sr-only, not removed. */}
      <Typography
        variant="h4"
        component="h1"
        sx={showHeading
          ? { fontFamily: tutorTokens.fontUI, color: tutorTokens.ink, mb: 1.5 }
          : visuallyHidden}
      >
        Agent Tutor
      </Typography>

      <Typography
        sx={{
          ...tutorTokens.type.body,
          fontFamily: tutorTokens.fontUI,
          color: tutorTokens.inkSoft,
        }}
      >
        {BODY_TEXT}
      </Typography>
    </Box>
  );

  if (!standalone) {
    return (
      <Box
        sx={{
          minHeight: 'clamp(420px, 62vh, 640px)',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tutorTokens.surface,
          px: tutorTokens.layout.gutter,
          py: { xs: 8, sm: 10 },
          boxSizing: 'border-box',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', backgroundColor: tutorTokens.surface }}>
      {/* Outer bar is not itself interactive — it's the full-width chrome,
          same as the real headers (StudentTutor.js, TutorWorkspace.js). Only
          the wordmark-sized box below carries role="button"/tabIndex/the
          click+keydown handlers, so the clickable/focusable region matches
          the real pages exactly instead of the whole bar being one target. */}
      <Box
        sx={{
          backgroundColor: adminColors.sidebar,
          color: '#fff',
          px: { xs: 2, sm: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label="Back to chat"
          onClick={() => navigate('/chat')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/chat'); } }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            ...focusRingSx,
          }}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography sx={{ fontWeight: 800 }}>LearnAndMasterAI</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: tutorTokens.layout.gutter,
          // Asymmetric top/bottom padding, not a `transform: translateY`,
          // to optically re-center the badge against true viewport center
          // (it would otherwise center within the space *below* the fixed
          // header, landing ~28px low — half the header's own height). A
          // few px less on top than bottom nudges the centered content up
          // by that same amount without a fixed offset stacked on top of
          // `alignItems: center`. That distinction matters on a short
          // viewport (a landscape phone, a laptop with browser chrome
          // eating height): when the badge + copy + padding don't fit in
          // the available space, this box just grows past its `flex: 1`
          // share (default flex-item `min-height: auto`, not `0`) and the
          // page scrolls — padding stays put, nothing crowds the header.
          // A `translateY` offset, by contrast, keeps firing even once
          // there's no room left to center within, pushing content up
          // toward/under the header instead of scrolling past it.
          pt: { xs: 4.5, sm: 6.5 },
          pb: { xs: 11.5, sm: 13.5 },
          boxSizing: 'border-box',
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

export default AgentTutorComingSoon;
